import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Pencil, Trash2, Check, X, StickyNote } from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};
const dayKey = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function NotesModal({ open, onOpenChange, record, canManageNotes, onCountChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const recordId = record?.id;
  const firma = record?.values?.firma || record?.values?.id || "Müşteri";

  const load = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/records/${recordId}/notes`);
      setNotes(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    if (open) {
      setText("");
      setEditId(null);
      load();
    }
  }, [open, load]);

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/records/${recordId}/notes`, { text });
      setNotes((n) => [data.note, ...n]);
      setText("");
      onCountChange?.(recordId, data.count);
      toast.success("Not eklendi");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/notes/${id}`, { text: editText });
      setNotes((n) => n.map((x) => (x.id === id ? data : x)));
      setEditId(null);
      toast.success("Not güncellendi");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const removeNote = async (id) => {
    if (!window.confirm("Bu not silinsin mi?")) return;
    try {
      const { data } = await api.delete(`/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
      onCountChange?.(recordId, data.count);
      toast.success("Not silindi");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  // group notes by day for the date-by-date timeline
  const groups = [];
  let currentDay = null;
  notes.forEach((n) => {
    const d = dayKey(n.created_at);
    if (d !== currentDay) {
      currentDay = d;
      groups.push({ day: d, items: [] });
    }
    groups[groups.length - 1].items.push(n);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="notes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-primary" /> Görüşme Notları
          </DialogTitle>
          <DialogDescription>
            <b>{firma}</b> · {notes.length} not
          </DialogDescription>
        </DialogHeader>

        {/* add box */}
        <div className="flex gap-2 items-end">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Görüşme notu yazın..."
            rows={2}
            className="resize-none"
            data-testid="note-input"
          />
          <Button onClick={addNote} disabled={saving || !text.trim()} data-testid="note-add-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>

        {/* timeline */}
        <div className="flex-1 overflow-y-auto grid-scroll -mr-2 pr-2 mt-1">
          {loading ? (
            <div className="flex justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10">Henüz not yok.</p>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.day}>
                  <div className="sticky top-0 bg-popover py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.day}
                  </div>
                  <div className="space-y-2 mt-1">
                    {g.items.map((n) => (
                      <div
                        key={n.id}
                        className="rounded-lg border border-border p-3 bg-secondary/30"
                        data-testid={`note-item-${n.id}`}
                      >
                        {editId === n.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={2}
                              data-testid="note-edit-input"
                            />
                            <div className="flex gap-2 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => setEditId(null)}>
                                <X className="w-4 h-4" />
                              </Button>
                              <Button size="sm" onClick={() => saveEdit(n.id)} data-testid="note-edit-save">
                                <Check className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[11px] text-muted-foreground">
                                {n.author_name} · {fmtDate(n.created_at)}
                                {n.updated_at !== n.created_at && " (düzenlendi)"}
                              </span>
                              {canManageNotes && (
                                <div className="flex gap-1">
                                  <button
                                    className="text-muted-foreground hover:text-primary"
                                    onClick={() => {
                                      setEditId(n.id);
                                      setEditText(n.text);
                                    }}
                                    data-testid={`note-edit-${n.id}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    className="text-muted-foreground hover:text-destructive"
                                    onClick={() => removeNote(n.id)}
                                    data-testid={`note-delete-${n.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
